import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Table,
  Image,
  Space,
  Button,
  Modal,
  notification,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";

import Imageurldata from "../../../../../Imageurldata";
import config from "../../../../../config/config";
import RecipeVideoCreate from "./Create";
import RecipeVideoEdit from "./Edit";
import RecipeVideoOrdering from "./Ordering";

const { confirm } = Modal;

function MoreRecipeVideoIndex() {
  const [loading, setLoading] = useState(false);
  // const [videos, setVideos] = useState([]);
  const [independentVideos, setIndependentVideos] = useState([]);
  const [categoryVideos, setCategoryVideos] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);

  const moduleName = "Recipe Video";
  const moduleNamePlural = "Recipe Videos";
  const base_url = `${config.api_url}laxmis-kitchen`;

  /* ===================== FETCH ===================== */
  const fetchRecipeVideos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${base_url}/recipe/all`);
      const { categories = [], recipes = [] } = res.data;

      /* ================= INDEPENDENT VIDEOS ================= */
      const independent = recipes.flatMap((recipe) =>
        (recipe.items || []).map((video) => ({
          ...video,
          parentId: recipe._id,
          source: "RECIPE",
          categoryTitle: "Independent Recipes",
        }))
      );

      independent.sort((a, b) => b.position - a.position);
      setIndependentVideos(independent);

      /* ================= CATEGORY VIDEOS ================= */
      const categoryFlat = categories.flatMap((category) =>
        (category.items || []).map((video) => ({
          ...video,
          parentId: category._id,
          source: "CATEGORY",
          categoryTitle: category.categoryTitle,
        }))
      );

      categoryFlat.sort((a, b) => b.position - a.position);
      setCategoryVideos(categoryFlat);
    } catch (err) {
      notification.error({ message: "Failed to fetch recipe videos" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipeVideos();
  }, []);

  /* ===================== DELETE ===================== */
  const handleDelete = async (videoId) => {
    try {
      await axios.delete(`${base_url}/recipe/video/delete`, {
        data: { videoId }, // Important: delete payload expects `videoId`
      });

      notification.success({
        message: `${moduleName} deleted successfully`,
      });

      // Refresh video list
      fetchRecipeVideos();
    } catch (err) {
      notification.error({
        message: "Delete failed",
      });
    }
  };

  const showDeleteConfirm = (videoId) => {
    confirm({
      title: `Are you sure you want to delete this ${moduleName}?`,
      icon: <ExclamationCircleOutlined />,
      okText: "YES",
      okType: "danger",
      cancelText: "NO",
      onOk() {
        return handleDelete(videoId);
      },
    });
  };

  /* ===================== TABLE COLUMNS ===================== */
  const columns = [
    {
      title: "Recipe Title",
      dataIndex: "title",
      width: 270,
    },
    {
      title: "Date",
      dataIndex: "date2",
      width: 170,
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Video URL",
      dataIndex: "videoUrl",
      width: 250,
      render: (url) => <span style={{ wordBreak: "break-all" }}>{url}</span>,
    },
    {
      title: "Image",
      width: 180,
      render: (_, record) => (
        <Image
          src={Imageurldata + record.desktopImageUrl.replaceAll("\\", "/")}
          width={100}
          height={80}
          style={{ objectFit: "cover", borderRadius: 6 }}
          fallback="/no-image.png"
        />
      ),
    },
    {
      title: "Action",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            onClick={() => {
              setSelectedVideo(record);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
          <Button type="danger" onClick={() => showDeleteConfirm(record._id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const categoryColumns = [
    {
      title: "Recipe Title",
      dataIndex: "title",
      width: 270,
    },
    {
      title: "Category Name",
      dataIndex: "categoryTitle",
      width: 200,
    },
    {
      title: "Date",
      dataIndex: "date2",
      width: 170,
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Video URL",
      dataIndex: "videoUrl",
      width: 250,
      render: (url) => <span style={{ wordBreak: "break-all" }}>{url}</span>,
    },
    {
      title: "Image",
      width: 180,
      render: (_, record) => (
        <Image
          src={Imageurldata + record.desktopImageUrl.replaceAll("\\", "/")}
          width={100}
          height={80}
          style={{ objectFit: "cover", borderRadius: 6 }}
          fallback="/no-image.png"
        />
      ),
    },
    {
      title: "Action",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            onClick={() => {
              setSelectedVideo(record);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
          <Button type="danger" onClick={() => showDeleteConfirm(record._id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* BREADCRUMB */}
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>{moduleNamePlural}</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        <Col span={24}>
          <Row style={{ marginBottom: 20 }}>
            <Col span={24}>
              <Space>
                <Button type="primary" onClick={() => setCreateOpen(true)}>
                  Add Recipe Video
                </Button>
                <Button type="primary" onClick={() => setOrderOpen(true)}>
                  Reorder Recipe Videos
                </Button>
              </Space>
            </Col>
          </Row>
          {/* ================= INDEPENDENT RECIPE VIDEOS ================= */}
          <Card
            type="inner"
            title="Independent Recipe Videos"
            style={{ marginBottom: 30 }}
          >
            <Table
              loading={loading}
              rowKey="_id"
              columns={columns}
              dataSource={independentVideos}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: "No independent recipe videos found" }}
              footer={() =>
                `Total Independent Videos: ${independentVideos.length}`
              }
            />
          </Card>

          {/* ================= CATEGORY VIDEOS ================= */}
          <Card
            type="inner"
            title="Category Recipe Videos"
            style={{ marginBottom: 30 }}
          >
            <Table
              loading={loading}
              rowKey="_id"
              columns={categoryColumns}
              dataSource={categoryVideos}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: "No category recipe videos found" }}
              footer={() => `Total Category Videos: ${categoryVideos.length}`}
            />
          </Card>

          {/* CREATE */}
          <RecipeVideoCreate
            isVisible={createOpen}
            handleClose={() => {
              setCreateOpen(false);
              fetchRecipeVideos();
            }}
            base_url={base_url}
          />

          {/* EDIT */}
          {selectedVideo && (
            <RecipeVideoEdit
              isVisible={editOpen}
              dbData={selectedVideo}
              handleClose={() => {
                setEditOpen(false);
                setSelectedVideo(null);
                fetchRecipeVideos();
              }}
              base_url={base_url}
            />
          )}

          <RecipeVideoOrdering
            isOpen={orderOpen}
            recipeVideos={independentVideos}
            base_url={base_url}
            onClose={(refresh) => {
              setOrderOpen(false);
              if (refresh) fetchRecipeVideos();
            }}
          />
        </Col>
      </Row>
    </div>
  );
}

export default MoreRecipeVideoIndex;
